import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Channel, Meeting, Message } from "@agentsaas/protocol";
import { useServices } from "../app/services";
import { isEvent, useChannel } from "./realtime";
import { mergeMessages } from "../rooms/model";

/**
 * P5 会议数据：列表 / 详情走 react-query；会议室订阅 meeting:<mid>（meeting.state 整体替换详情，meeting.turn 更新发言者与轮次，
 * message.created 并入消息流）。消息历史来自会议的 conversation（messageList）；同时订阅 conversation:<cid>，
 * 两边按 id 去重（文档只说 meeting 通道带 message.*，保险起见都接）。
 */

export const mk = {
  list: (pid: string) => ["meetings", pid] as const,
  one: (mid: string) => ["meeting", mid] as const,
};

export function useMeetings(pid: string | undefined) {
  const { api } = useServices();
  return useQuery({
    queryKey: mk.list(pid ?? ""),
    enabled: !!pid,
    queryFn: async () => (await api.call("meetingList", { params: { pid: pid! }, query: { limit: 100 } })).items,
  });
}

export function useMeeting(mid: string | undefined) {
  const { api } = useServices();
  return useQuery({ queryKey: mk.one(mid ?? ""), enabled: !!mid, queryFn: () => api.call("meetingGet", { params: { mid: mid! } }) });
}

/** 写回一个会议（详情 + 列表） */
export function putMeeting(qc: QueryClient, m: Meeting) {
  qc.setQueryData(mk.one(m.id), m);
  qc.setQueryData<Meeting[]>(mk.list(m.projectId), (xs) => (xs ? (xs.some((x) => x.id === m.id) ? xs.map((x) => (x.id === m.id ? m : x)) : [m, ...xs]) : xs));
}

/** 会议室实时：消息流 + 状态 */
export function useMeetingStream(meeting: Meeting | undefined) {
  const { api } = useServices();
  const qc = useQueryClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loaded, setLoaded] = useState(false);
  const alive = useRef(true);
  const mid = meeting?.id;
  const cid = meeting?.conversationId;

  const load = useCallback(async () => {
    if (!cid) return;
    try {
      // 会议消息有限（轮数 × 参与者 + 插话），按页拉全（最多 10 页）
      let cursor: string | undefined;
      for (let i = 0; i < 10; i++) {
        const page = await api.call("messageList", { params: { cid }, query: { limit: 100, ...(cursor ? { cursor } : {}) } });
        if (!alive.current) return;
        setMessages((cur) => mergeMessages(cur, page.items));
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }
    } catch {
      /* 拉历史失败时仍显示实时消息 */
    } finally {
      if (alive.current) setLoaded(true);
    }
  }, [api, cid]);

  useEffect(() => {
    alive.current = true;
    setMessages([]);
    setLoaded(false);
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const add = useCallback((m: Message) => setMessages((cur) => mergeMessages(cur, [m])), []);
  const patch = (fn: (m: Meeting) => Meeting) => {
    const cur = mid ? qc.getQueryData<Meeting>(mk.one(mid)) : undefined;
    if (cur) putMeeting(qc, fn(cur));
  };

  useChannel(mid ? (`meeting:${mid}` as Channel) : null, {
    onEvent(e) {
      if (isEvent(e, "meeting.state")) putMeeting(qc, e.data);
      else if (isEvent(e, "meeting.turn")) patch((m) => ({ ...m, currentSpeaker: e.data.speaker, round: e.data.round }));
      else if (isEvent(e, "message.created") || isEvent(e, "message.updated")) add(e.data);
    },
    onGap() {
      void load();
      if (mid) void qc.invalidateQueries({ queryKey: mk.one(mid) });
    },
  });
  useChannel(cid ? (`conversation:${cid}` as Channel) : null, {
    onEvent(e) {
      if (isEvent(e, "message.created") || isEvent(e, "message.updated")) add(e.data);
    },
  });

  return { messages, loaded, add };
}
